import { reduce } from 'ramda'

import { Allowance } from '../../../_common/useReportAllowances'

const minCellWidth = 120
const nameColumnWidth = 200

type Options = {
  showPersonal?: boolean
  showCommon?: boolean
}

const defaultOptions: Options = {
  showPersonal: true,
  showCommon: true,
}

const getOptionsWithDefaults = (options: Options = defaultOptions) => {
  return { ...defaultOptions, ...options }
}

export enum ColumnKey {
  Org = 'org',
  OrgGhost = 'orgGhost', // пустая ячейка
  Personal = 'personal',
  Common = 'common',
  Total = 'total',
}

export const createDinamicColumnKey = (allowance: Allowance) => {
  return `${ColumnKey.Personal}_${allowance}`
}

export const columnKeysLeft = [ColumnKey.Org]
export const columnKeysLeftHeaders = [columnKeysLeft, [ColumnKey.OrgGhost]]
export const columnKeysLeftHeadersNoSpan = [columnKeysLeft]

export const createColumnKeysMain = (allowances: Array<Allowance>, options?: Options) => {
  const { showCommon, showPersonal } = getOptionsWithDefaults(options)
  return [
    ...(showPersonal ? allowances.map(createDinamicColumnKey) : []),
    ...(showCommon ? [ColumnKey.Common] : []),
    ColumnKey.Total,
  ]
}

export const createColumnKeysMainHeaders = (allowances: Array<string>, options?: Options) => {
  const { showCommon, showPersonal } = getOptionsWithDefaults(options)
  if (showPersonal) {
    return [
      [ColumnKey.Personal, ...(showCommon ? [ColumnKey.Common] : []), ColumnKey.Total],
      allowances.map(createDinamicColumnKey),
    ]
  }
  return [[...(showCommon ? [ColumnKey.Common] : []), ColumnKey.Total]]
}

const headerCells = {
  [ColumnKey.Org]: 'Наименование заведения',
  [ColumnKey.OrgGhost]: '',
  [ColumnKey.Personal]: 'Индив. справки',
  [ColumnKey.Common]: 'Общие справки',
  [ColumnKey.Total]: 'Всего',
}

export const createHeaderCells = (allowances: Array<Allowance>) => {
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = allowance
      return acc
    },
    headerCells,
    allowances,
  )
}

const headerCellsProps = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth, rowSpan: 2 },
  [ColumnKey.OrgGhost]: { alignLeft: true, width: 0, stripPadding: true },
  [ColumnKey.Common]: { alignRight: true, rowSpan: 2, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, rowSpan: 2, minWidth: minCellWidth },
}

const headerCellsPropsNoSpan = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth },
  [ColumnKey.OrgGhost]: { alignLeft: true, width: 0, stripPadding: true },
  [ColumnKey.Common]: { alignRight: true, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, minWidth: minCellWidth },
}

export const createHeaderCellsProps = (allowances: Array<Allowance>, options?: Options) => {
  const { showPersonal } = getOptionsWithDefaults(options)
  if (!showPersonal) {
    return headerCellsPropsNoSpan
  }

  let index = 0
  return {
    [ColumnKey.Personal]: { alignCenter: true, colSpan: allowances.length, borderRight: true },
    ...reduce(
      (acc, allowance) => {
        const key = createDinamicColumnKey(allowance)
        acc[key] = { alignRight: true, borderRight: true, minWidth: minCellWidth }
        if (index === 0) {
          acc[key] = {
            alignRight: true,
            borderRight: true,
            ignoreFirstChild: true,
            minWidth: minCellWidth,
          }
        }
        if (index === allowances.length - 1) {
          acc[key] = {
            alignRight: true,
            ignoreLastChild: true,
            borderRight: true,
            minWidth: minCellWidth,
          }
        }
        index += 1
        return acc
      },
      headerCellsProps,
      allowances,
    ),
  }
}

const cellsProps = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth },
  [ColumnKey.Common]: { alignRight: true, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, minWidth: minCellWidth },
}

export const createCellsProps = (allowances: Array<Allowance>) => {
  let index = 0
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = { alignRight: true, borderRight: true, minWidth: minCellWidth }
      if (index === 0) {
        acc[key] = { alignRight: true, borderRight: true, ignoreFirstChild: true }
      }
      index += 1
      return acc
    },
    cellsProps,
    allowances,
  )
}

const totalCellsProps = {
  [ColumnKey.Org]: { alignRight: true, width: nameColumnWidth, bold: true },
  [ColumnKey.Common]: {
    alignRight: true,
    highlighted: true,
    bold: true,
    borderRight: true,
    minWidth: minCellWidth,
  },
  [ColumnKey.Total]: { alignRight: true, highlighted: true, bold: true, minWidth: minCellWidth },
}

export const createTotalCellsProps = (allowanceIDs: Array<string>) => {
  let index = 0
  return reduce(
    (acc, allowance) => {
      const key = createDinamicColumnKey(allowance)
      acc[key] = {
        alignRight: true,
        highlighted: true,
        bold: true,
        borderRight: true,
        minWidth: minCellWidth,
      }
      if (index === 0) {
        acc[key] = {
          alignRight: true,
          highlighted: true,
          bold: true,
          borderRight: true,
          ignoreFirstChild: true,
          minWidth: minCellWidth,
        }
      }
      index += 1
      return acc
    },
    totalCellsProps,
    allowanceIDs,
  )
}
